// 🔧 manifest.js
// ✅ Purpose: SAP AI Agents Challenge Experience manifest for the LGV Legacy Experience Factory
// ✅ Features:
// - Registers the SAP NOW Milan AI Agents Challenge as a purpose-built customer demo Experience
// - Defines Partner and Challenge terminology
// - Uses exactly thirteen fictional partner entries
// - Declares a fixed entry count so generic Small/Medium/Large sizing cannot duplicate or truncate partners
// - Records the known SAP event date as template metadata without inventing unconfirmed event hours
// - Leaves Detailed Scorecard criteria and judged evaluation seeding to Enhancements 5C and 5D
// - Fully block-commented for clarity and future devs

import {
  SAP_AI_AGENTS_CHALLENGE_CATEGORIES,
} from "./categories";

import {
  SAP_AI_AGENTS_CHALLENGE_ENTRIES,
} from "./entries";

import {
  adaptExperienceManifest,
  validateExperienceMetadata,
} from "../../core/experience_metadata_adapter";

export const SAP_AI_AGENTS_CHALLENGE_MANIFEST = {
  key: "sap_ai_agents_challenge",

  enabled: true,

  label: "SAP AI Agents Challenge",

  emoji: "🤖",

  entryLabel: "Partner",

  categoryLabel: "Challenge",

  voterLabel: "Challenge Guest",

  userProfiles: [
    {
      displayName: "Alessandra Moretti",
      participantType: "Challenge Manager",
    },
  ],

  description:
    "Creates a SAP NOW Milan AI Agents Challenge customer demo with thirteen partner teams and judge-ready challenge entries.",

  version: 1,

  categories:
    SAP_AI_AGENTS_CHALLENGE_CATEGORIES,

  defaultCategories:
    SAP_AI_AGENTS_CHALLENGE_CATEGORIES.map(
      (category) => category.name
    ),

  entries:
    SAP_AI_AGENTS_CHALLENGE_ENTRIES,

  // =====================================================
  // ✅ Fixed Experience size
  // =====================================================
  // This Experience represents a known thirteen-partner
  // challenge. Generic demo-size selection must not alter it.
  // =====================================================

  fixedEntryCount: 13,

  // =====================================================
  // ✅ Fixed customer-demo population
  // =====================================================
  // These values are explicit Experience requirements rather
  // than generic defaults. Demo Size therefore cannot reduce
  // the evaluator/evaluation volume required for the SAP demo.
  // =====================================================

  fixedUserCount: 30,

  fixedVoteCount: 250,

  // =====================================================
  // ✅ SAP Detailed Scorecard configuration
  // =====================================================
  // The production Points engine powers Detailed Scorecard.
  //
  // One 25-point factor contains five scored highlights.
  // Each highlight uses the same 1-5 scale requested for the
  // SAP AI Agents Challenge.
  //
  // Results remain unavailable to attendees. Event judges,
  // managers, and platform administrators are authorized by
  // the existing Results Visibility contract.
  // =====================================================

  scoring: {
    votingType: "points",

    maxBallotPoints: 25,

    resultsAudience: "judges",

    // Fixed SAP demo judge pool
    judgeCount: 19,

    leaderboardLimit: 13,

    factors: [
      {
        name:
          "AI Agent Evaluation",

        maxPoints: 25,

        sortOrder: 1,

        active: true,

        highlights: [
          {
            name: "Innovation",
            maxPoints: 5,
            sortOrder: 1,
            active: true,
          },
          {
            name: "Business Value",
            maxPoints: 5,
            sortOrder: 2,
            active: true,
          },
          {
            name: "User Experience",
            maxPoints: 5,
            sortOrder: 3,
            active: true,
          },
          {
            name: "SAP Integration",
            maxPoints: 5,
            sortOrder: 4,
            active: true,
          },
          {
            name: "Presentation",
            maxPoints: 5,
            sortOrder: 5,
            active: true,
          },
        ],
      },
    ],
  },

  experience: {
    eventName:
      "SAP NOW Milan — AI Agents Challenge",

    // =====================================================
    // ✅ Customer demo presentation metadata
    // =====================================================
    // These values describe the generated Experience for
    // customer demonstration purposes.
    //
    // They remain owned by the Experience manifest so future
    // industries can provide their own metadata without
    // changing Demo Builder logic.
    // =====================================================

    demoAudience:
      "SAP event organizers, partners, and judges",

    demoNarrative:
      "A realistic AI Agents Challenge showcasing fictional SAP partner teams evaluated through a governed judge-only Detailed Scorecard.",

    participantDescription:
      "Fictional SAP partner teams presenting AI agent solutions.",

    experienceType:
      "customer_demo",

    eventDate:
      "2026-10-14",

    location:
      "Milan, Italy",

    participantType:
      "SAP Partner",

    entryCount: 13,

    participantData:
      "fictional_demo_partners",
  },

  content: {},

  seo: {},

  map: {},

  schedule: {},

  defaults: {
    entryCount: 13,

    // User and evaluation volumes remain generic until
    // the realistic judged-evaluation seeding work in 5D.
    userCount: 30,

    voteCount: 250,
  },
};

// ============================================================
// Generic Experience Metadata Consumption
//
// Purpose:
// - Provide normalized metadata contract
// - Keep SAP-specific definitions inside manifest
// ============================================================

export const SAP_EXPERIENCE_METADATA =
  adaptExperienceManifest(
    SAP_AI_AGENTS_CHALLENGE_MANIFEST
  );


export const SAP_EXPERIENCE_METADATA_VALIDATION =
  validateExperienceMetadata(
    SAP_EXPERIENCE_METADATA
  );
